import http from '@/api/http'
import { TReportJobStatus } from '@/types/report'

import {
  TGetPredictionsPreviewResponse,
  TGetPredictionParams,
  TGetCheckPredictionsResponse,
} from './types'

type FilterValue = string | number | Array<string | number>

// Lista vai como "a,b", o formato que o backend lê.
const toQueryValue = (value: FilterValue): string =>
  Array.isArray(value) ? value.join(',') : String(value)

export const getPredictionsPreview = async (filters: TGetPredictionParams) => {
  const {
    coSphere,
    coRegion,
    tpLocation,
    coEducationStageDisplay,
    nuVersao,
    ...rest
  } = filters

  const params: Record<string, unknown> = Object.fromEntries(
    Object.entries(rest).filter(
      ([, v]) => v !== null && v !== undefined && v !== false && v !== ''
    )
  )

  if (nuVersao !== undefined && nuVersao !== '' && nuVersao !== null) {
    params.nu_versao = nuVersao
  }

  if (coSphere) {
    params.co_sphere = toQueryValue(coSphere)
  }

  if (coRegion) {
    params.co_region = toQueryValue(coRegion)
  }

  if (tpLocation !== undefined) {
    params.tp_location = tpLocation
  }

  const response = await http.get<TGetPredictionsPreviewResponse>(
    '/predictions/preview/',
    {
      params,
    }
  )

  return response.data
}

export const getPredictions = async (filters: TGetPredictionParams) => {
  const {
    coSphere,
    coRegion,
    tpLocation,
    coEducationStageDisplay,
    nuVersao,
    ...rest
  } = filters
  const params: Record<string, unknown> = { ...rest }

  if (nuVersao !== undefined && nuVersao !== '' && nuVersao !== null) {
    params.nu_versao = nuVersao
  }

  if (coSphere) {
    params.co_sphere = toQueryValue(coSphere)
  }

  if (coRegion) {
    params.co_region = toQueryValue(coRegion)
  }

  if (tpLocation !== undefined) {
    params.tp_location = tpLocation
  }

  const response = await http.get<string>('/predictions/', {
    params,
  })

  return response.data
}

export const getCheckPredictions = async () => {
  const response = await http.get<TGetCheckPredictionsResponse>(
    '/prediction-processing/status/'
  )

  return response.data
}

export type { TReportJobStatus }

export const requestPredictionReport = async (
  filters: TGetPredictionParams
): Promise<{ jobId: string; status: string; message: string }> => {
  const f = filters

  const params: Record<string, string> = {}

  if (f.nuCensusYear) params.nu_census_year = toQueryValue(f.nuCensusYear)
  if (f.nuYearPrediction) params.nu_year_prediction = f.nuYearPrediction
  if (f.nuVersao !== undefined && f.nuVersao !== '' && f.nuVersao !== null)
    params.nu_versao = toQueryValue(f.nuVersao)
  if (f.isAllStages !== undefined && f.isAllStages !== false)
    params.is_all_stages = String(f.isAllStages)
  if (f.stPnldService) params.st_pnld_service = String(f.stPnldService)
  if (f.coEducationStage) params.co_education_stage = f.coEducationStage
  if (f.coCity) params.co_city = f.coCity
  if (f.coUf) params.co_uf = f.coUf
  if (f.coEntity) params.co_entity = f.coEntity
  if (f.tpDependency) params.tp_dependency = f.tpDependency
  if (
    f.tpLocation !== undefined &&
    f.tpLocation !== null &&
    f.tpLocation !== ''
  )
    params.tp_location = f.tpLocation
  if (f.coSphere) params.co_sphere = toQueryValue(f.coSphere)
  if (f.coRegion) params.co_region = toQueryValue(f.coRegion)
  if (f.typePrediction) params.type_prediction = f.typePrediction

  const queryString = new URLSearchParams(params).toString()

  const response = await http.post<{
    job_id: string
    status: string
    message: string
  }>(`/predictions/request-report/?${queryString}`)

  const data = response.data as unknown as Record<string, string>

  return {
    jobId: data.job_id ?? data.jobId,
    status: response.data.status,
    message: response.data.message,
  }
}

export const getPredictionReportStatus = async (
  jobId: string
): Promise<TReportJobStatus> => {
  const response = await http.get<TReportJobStatus>(
    `/predictions/report-status/${jobId}/`
  )
  return response.data
}

export const cancelAllPredictions = async (): Promise<{
  cancelled: number
}> => {
  const response = await http.post<{
    cancelled: number
  }>('/prediction-processing/cancel-all/')

  return response.data
}
